import { createFileRoute, Link } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { useState } from 'react'
import { noop, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { Button, buttonVariants } from '@/components/ui/button'
import { AuthCard } from '@/routes/_app/-components/shared/auth-card'
import { AuthPending } from '@/routes/_app/-components/app-layout-skeleton'
import { Avatar } from '@/routes/_app/-components/shared/avatar'
import { trpc } from '@/main'

export const Route = createFileRoute('/_app/_public/employee-invitation/$token')({
  context: ({ params }) => ({
    employeeInvitationInfoQueryOptions: trpc.employees.getEmployeeInvitationInfo.queryOptions({ token: params.token }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.employeeInvitationInfoQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: AuthPending,
})

// Consent page for the guard against silently linking an existing account: a company tried to add
// this person (matched by email) as an employee, and the membership is only created if they accept.
function RouteComponent() {
  const { token } = Route.useParams()
  const { t } = useLingui()
  const [result, setResult] = useState<'accepted' | 'declined' | 'error' | null>(null)
  // Responding CONSUMES the token, and the global post-write revalidation refetches this page's own query
  // as part of that same write — so `info` is null by the time the outcome card renders, and the name
  // below would fall back to the product's ("You're now an employee of D'Anchiano"). Latched at click
  // time, while the invitation is still readable.
  const [respondedCompany, setRespondedCompany] = useState<string | null>(null)

  const { data: info } = useSuspenseQuery(Route.useRouteContext().employeeInvitationInfoQueryOptions)
  const respond = useMutation(trpc.employees.respondEmployeeInvitation.mutationOptions())

  const handleRespond = (accept: boolean) => {
    setRespondedCompany(info?.companyName ?? null)
    respond.mutateAsync({ token, accept }).then(
      (data) => setResult(data.accepted ? 'accepted' : 'declined'),
      () => setResult('error'),
    )
  }

  const companyName = respondedCompany ?? info?.companyName ?? 'D’Anchiano'

  const goToSignIn = (
    <Link to="/login" className={buttonVariants({ className: 'w-full' })}>
      {t`Go to sign in`}
    </Link>
  )

  return info === null && !result ? (
    <AuthCard
      title={t`This invitation is no longer available`}
      subtitle={t`The link may have expired or already been used.`}
    >
      {goToSignIn}
    </AuthCard>
  ) : result === 'accepted' ? (
    <AuthCard
      title={t`You're now an employee of ${companyName}`}
      subtitle={t`Your psychographic profile is now shared with ${companyName}. Sign in to see your profile, exactly as they see it. You can leave this company at any time from your account.`}
    >
      {goToSignIn}
    </AuthCard>
  ) : result === 'declined' ? (
    <div className="mx-auto flex max-w-[480px] flex-col items-center gap-6 p-6">
      <div className="text-center">
        <h1 className="text-2xl font-semibold">{t`Invitation declined`}</h1>
        <p className="text-muted-foreground mt-1">{t`No problem — nothing has been shared with ${companyName}.`}</p>
      </div>
    </div>
  ) : result === 'error' ? (
    <AuthCard title={t`Something went wrong`} subtitle={t`The link may have expired or already been used.`}>
      {goToSignIn}
    </AuthCard>
  ) : (
    <AuthCard
      header={
        <div className="flex flex-col items-center gap-4 text-center">
          {info?.companyImage && <Avatar size="4xl" type="company" url={info.companyImage} name={companyName} />}
          <div>
            <h1 className="text-2xl font-semibold">{t`${companyName} wants to add you as an employee`}</h1>
            <p className="text-muted-foreground mt-1">
              {t`If you accept, your psychographic profile (Competencies, Values and Personality) will be shared with ${companyName}. You can dissociate from this company at any time.`}
            </p>
          </div>
        </div>
      }
      contentClassName="flex flex-col gap-3"
    >
      <Button onClick={() => handleRespond(true)} disabled={respond.isPending}>
        {t`Accept invitation`}
      </Button>
      <Button variant="outline" onClick={() => handleRespond(false)} disabled={respond.isPending}>
        {t`Decline`}
      </Button>
    </AuthCard>
  )
}
